import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Loader2, Upload, FileText, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12 } }
};

const specializations = [
  'Social Media Management',
  'Content Creation',
  'Creative Strategy',
  'Influencer & Partnerships',
  'Community Management',
  'Video Editing',
  'Graphic Design',
  'Creative Direction',
  'Copywriting',
  'Photography',
];

const benefits = [
  'Access to curated opportunities',
  'Direct brand connections',
  'Roles aligned with your expertise',
  'No mass applications',
];

export default function TalentPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedResume, setUploadedResume] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    portfolio_url: '',
    linkedin_url: '',
    specialization: [],
    experience_level: '',
    availability: '',
    bio: '',
    resume_url: '',
  });

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleSpecialization = (spec) => {
    setFormData((prev) => ({
      ...prev,
      specialization: prev.specialization.includes(spec)
        ? prev.specialization.filter((s) => s !== spec)
        : [...prev.specialization, spec],
    }));
  };

  const handleResumeSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowed.includes(file.type)) {
      toast.error('Please upload a PDF or Word document.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be under 10MB.');
      return;
    }

    setResumeFile(file);
    setIsUploading(true);

    const fd = new FormData();
    fd.append('file', file);

    try {
      const res = await axios.post(`${API}/upload/resume`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadedResume({ id: res.data.file_id, name: res.data.filename, path: res.data.path });
      updateField('resume_url', `${API}/files/${res.data.path}`);
      toast.success('Resume uploaded successfully.');
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload resume.');
      setResumeFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  const removeResume = () => {
    setResumeFile(null);
    setUploadedResume(null);
    updateField('resume_url', '');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const isFormValid = () =>
    formData.full_name &&
    formData.email &&
    formData.portfolio_url &&
    formData.specialization.length > 0 &&
    formData.experience_level &&
    formData.availability &&
    formData.bio;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) {
      toast.error('Please fill in all required fields.');
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/talent`, formData);
      setIsSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="pt-20 min-h-screen bg-background" data-testid="talent-success">
        <div className="container-main py-24 md:py-32">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 bg-butter rounded-full flex items-center justify-center mx-auto mb-8">
              <Check className="h-10 w-10 text-foreground" />
            </div>
            <h1 className="font-serif text-3xl md:text-4xl font-medium mb-6">You're in.</h1>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-4">
              Thanks for applying to join the Muse Network. Our team will review your profile and portfolio.
            </p>
            <p className="text-muted-foreground text-sm">
              Approved talent are added to our curated network, and we'll reach out when opportunities align with your skills and goals.
            </p>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-44" data-testid="talent-page">
      {/* Hero */}
      <section className="py-16 md:py-24" data-testid="talent-hero">
        <div className="container-main">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="max-w-3xl mx-auto text-center"
          >
            <motion.h1 variants={fadeInUp} className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-tight mb-6">
              Join the Muse Network
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
              For social and creative professionals ready to work with modern brands.
            </motion.p>
          </motion.div>
        </div>
      </section>

      <div className="border-t border-border" />

      {/* Body */}
      <section className="py-16 md:py-24" data-testid="talent-body">
        <div className="container-main">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={stagger}
            className="max-w-2xl mx-auto"
          >
            <motion.p variants={fadeInUp} className="text-muted-foreground text-base md:text-lg leading-relaxed mb-4">
              Muse connects talent with curated opportunities across social media and creative roles.
            </motion.p>
            <motion.p variants={fadeInUp} className="text-muted-foreground text-base md:text-lg leading-relaxed mb-12">
              We don't operate like a job board. We focus on alignment, quality, and long-term fit.
            </motion.p>

            <motion.div variants={fadeInUp}>
              <p className="text-sm tracking-widest uppercase text-muted-foreground mb-6">What you get</p>
              <div className="space-y-4 mb-12">
                {benefits.map((b, i) => (
                  <div key={i} className="flex items-center gap-3" data-testid={`talent-benefit-${i}`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-butter flex-shrink-0" />
                    <span className="text-foreground">{b}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {!showForm ? (
              <motion.div variants={fadeInUp}>
                <button
                  onClick={() => setShowForm(true)}
                  className="inline-flex items-center gap-2 bg-butter text-foreground font-medium text-sm px-8 py-3.5 rounded-full hover:bg-butter-dark transition-colors duration-300"
                  data-testid="show-form-btn"
                >
                  Join the Network <ArrowRight className="h-4 w-4" />
                </button>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                <form onSubmit={handleSubmit} className="bg-secondary p-8 md:p-10 border border-border" data-testid="talent-form">
                  <h3 className="font-serif text-2xl font-medium mb-8">Apply to Join</h3>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="text"
                        placeholder="Your name"
                        value={formData.full_name}
                        onChange={(e) => updateField('full_name', e.target.value)}
                        required
                        data-testid="input-full-name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="email"
                        placeholder="you@email.com"
                        value={formData.email}
                        onChange={(e) => updateField('email', e.target.value)}
                        required
                        data-testid="input-email"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Portfolio URL <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="url"
                        placeholder="https://yourportfolio.com"
                        value={formData.portfolio_url}
                        onChange={(e) => updateField('portfolio_url', e.target.value)}
                        required
                        data-testid="input-portfolio"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        LinkedIn URL (Optional)
                      </label>
                      <Input
                        type="url"
                        placeholder="https://linkedin.com/in/you"
                        value={formData.linkedin_url}
                        onChange={(e) => updateField('linkedin_url', e.target.value)}
                        data-testid="input-linkedin"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-3">
                        Specialization <span className="text-red-500">*</span>
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        {specializations.map((spec) => (
                          <div key={spec} className="flex items-center gap-2">
                            <Checkbox
                              id={spec}
                              checked={formData.specialization.includes(spec)}
                              onCheckedChange={() => toggleSpecialization(spec)}
                              data-testid={`checkbox-${spec.toLowerCase().replace(/\s+/g, '-')}`}
                            />
                            <label htmlFor={spec} className="text-sm cursor-pointer">
                              {spec}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Experience Level <span className="text-red-500">*</span>
                      </label>
                      <Select
                        value={formData.experience_level}
                        onValueChange={(value) => updateField('experience_level', value)}
                      >
                        <SelectTrigger data-testid="select-experience">
                          <SelectValue placeholder="Select experience level" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="junior">Junior (0-2 years)</SelectItem>
                          <SelectItem value="mid">Mid-level (2-5 years)</SelectItem>
                          <SelectItem value="senior">Senior (5-8 years)</SelectItem>
                          <SelectItem value="lead">Lead/Director (8+ years)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Availability <span className="text-red-500">*</span>
                      </label>
                      <Select
                        value={formData.availability}
                        onValueChange={(value) => updateField('availability', value)}
                      >
                        <SelectTrigger data-testid="select-availability">
                          <SelectValue placeholder="Select availability" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="immediate">Immediately available</SelectItem>
                          <SelectItem value="2-weeks">Available in 2 weeks</SelectItem>
                          <SelectItem value="1-month">Available in 1 month</SelectItem>
                          <SelectItem value="exploring">Just exploring</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Brief Bio <span className="text-red-500">*</span>
                      </label>
                      <Textarea
                        placeholder="Tell us about yourself, your experience, and what you're looking for..."
                        value={formData.bio}
                        onChange={(e) => updateField('bio', e.target.value)}
                        className="min-h-[120px] resize-none"
                        required
                        data-testid="textarea-bio"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Resume (Optional)
                      </label>
                      <p className="text-xs text-muted-foreground mb-3">PDF or Word document, max 10MB</p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleResumeSelect}
                        className="hidden"
                        data-testid="input-resume-file"
                      />
                      {!uploadedResume ? (
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={isUploading}
                          className="w-full border-2 border-dashed border-border hover:border-butter/50 rounded-lg p-6 flex flex-col items-center gap-2 transition-colors duration-300 disabled:opacity-50"
                          data-testid="resume-upload-btn"
                        >
                          {isUploading ? (
                            <>
                              <Loader2 className="h-6 w-6 text-butter animate-spin" />
                              <span className="text-sm text-muted-foreground">Uploading...</span>
                            </>
                          ) : (
                            <>
                              <Upload className="h-6 w-6 text-muted-foreground" />
                              <span className="text-sm text-muted-foreground">Click to upload your resume</span>
                            </>
                          )}
                        </button>
                      ) : (
                        <div className="flex items-center gap-3 border border-border rounded-lg p-4 bg-background" data-testid="resume-uploaded">
                          <FileText className="h-5 w-5 text-butter flex-shrink-0" />
                          <span className="text-sm text-foreground truncate flex-1">{uploadedResume.name}</span>
                          <button
                            type="button"
                            onClick={removeResume}
                            className="text-muted-foreground hover:text-foreground transition-colors"
                            data-testid="resume-remove-btn"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={!isFormValid() || isSubmitting}
                      className="inline-flex items-center justify-center gap-2 bg-butter text-foreground font-medium text-sm px-8 py-3.5 rounded-full hover:bg-butter-dark transition-colors duration-300 w-full disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="submit-btn"
                    >
                      {isSubmitting ? (
                        <><Loader2 className="h-4 w-4 animate-spin" /> Submitting...</>
                      ) : (
                        <>Join the Network <ArrowRight className="h-4 w-4" /></>
                      )}
                    </button>
                  </div>
                </form>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
